import { useState } from 'react';
import { HistoryChart, type ChartSeries, type ChartBand, type ChartBars, type ChartRefLine } from './HistoryChart';
import { madFilter, sanitize } from './outliers';
import { mean } from './chartMath';

interface Props {
  width: number;
  height: number;
  series: ChartSeries[];
  bands?: ChartBand[];
  // Passed straight through to HistoryChart, unfiltered — outlier removal
  // only applies to `series` (a bar has no `filterable` flag of its own).
  bars?: ChartBars[];
  yLabel?: string;
  // Unit for the right-hand scale, when any series sets `axis: 'right'`.
  // Forwarded to HistoryChart and used for the average label of those series.
  yLabelRight?: string;
  // Set when the API downsampled server-side. Switches the card into
  // "aggregated" mode: no outlier control (MAD-filtering already-aggregated
  // points is meaningless) and a caption stating the bucket width.
  bucketMs?: number;
  // Set when the API hit its raw-row scan cap. Surfaces as a muted caption
  // rather than an error — the chart is still honest about what it drew.
  truncated?: boolean;
  sampleCount?: number;
}

// Human-readable bucket width for the caption, e.g. "hourly", "15 min", "3 h".
function describeBucket(bucketMs: number): string {
  const minutes = Math.round(bucketMs / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? 'hourly' : `${hours} h`;
}

// HistoryChart wrapper that removes outliers from the measured (filterable)
// series by default, with a "Show all data" checkbox to reveal the raw points.
// Reference series (no `filterable` flag) always pass through untouched.
// Series flagged `average` also get a flat mean line — computed here, AFTER
// filtering, so the labelled value always matches the line that's drawn.
export function FilterableHistoryChart({ width, height, series, bands = [], bars, yLabel, yLabelRight, bucketMs, truncated, sampleCount }: Props) {
  const [showAll, setShowAll] = useState(false);
  const bucketed = bucketMs !== undefined;
  const hasFilterable = series.some((s) => s.filterable) && !bucketed;

  // Bucketed data skips the Hampel test (a local outlier test on points that
  // are already per-bucket medians is meaningless) but still gets the hard
  // prefilter, so open-circuit sentinels that survived the backend median
  // can't blow the y-domain open. `showAll` keeps its raw-mode meaning:
  // reveal every point exactly as fetched.
  const shown = bucketed
    ? series.map((s) => (s.filterable ? { ...s, data: sanitize(s.data) } : s))
    : showAll
      ? series
      : series.map((s) => (s.filterable ? { ...s, data: madFilter(s.data) } : s));

  const hidden = hasFilterable && !showAll
    ? series.reduce((n, s, i) => n + (s.filterable ? s.data.length - shown[i]!.data.length : 0), 0)
    : 0;

  // The mean line inherits its series' axis and unit. Without that a right-axis
  // series' average would be drawn against the LEFT domain and labelled with
  // the left unit — silently wrong, at a wrong-but-plausible height.
  const refLines: ChartRefLine[] = shown.flatMap((s, i) => {
    if (!s.average) return [];
    const m = mean(s.data);
    if (m === null) return [];
    const unit = s.axis === 'right' ? yLabelRight : yLabel;
    return [{
      v: m,
      label: `avg ${m.toFixed(1)}${unit ? ` ${unit}` : ''}`,
      colorIndex: i,
      ...(s.axis !== undefined ? { axis: s.axis } : {}),
    }];
  });

  return (
    <div>
      {(hasFilterable || bucketed || truncated) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 'var(--sp-2)', marginBottom: 'var(--sp-1)' }}>
          {truncated && (
            <span data-truncated-caption className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
              showing the most recent {sampleCount ?? 0} samples
            </span>
          )}
          {bucketed && (
            <span data-bucket-caption className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
              {describeBucket(bucketMs)} medians
            </span>
          )}
          {hasFilterable && !showAll && hidden > 0 && (
            <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{hidden} hidden</span>
          )}
          {hasFilterable && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)', fontSize: 'var(--fs-sm)', color: 'var(--muted)' }}>
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
              Show all data
            </label>
          )}
        </div>
      )}
      <HistoryChart
        width={width}
        height={height}
        series={shown}
        bands={bands}
        refLines={refLines}
        {...(bars !== undefined ? { bars } : {})}
        {...(yLabel !== undefined ? { yLabel } : {})}
        {...(yLabelRight !== undefined ? { yLabelRight } : {})}
      />
    </div>
  );
}
